import { HabitSchedule } from '@griply/types';

import { isPlanMemoDisabled } from '../plan-memo';

type ScheduleCacheEntry<TResult> = {
  // createPlannedOccurrenceForDate reassigns this array on a schedule it already read from.
  plannedOccurrences: HabitSchedule['plannedOccurrences'];
  result: TResult;
};

// Keyed on the schedule object, which the converter recreates on every edit, so this self-invalidates.
export const memoizeBySchedule = <TResult>(
  compute: (schedule: HabitSchedule) => TResult,
): ((schedule: HabitSchedule) => TResult) => {
  const cache = new WeakMap<HabitSchedule, ScheduleCacheEntry<TResult>>();

  return (schedule: HabitSchedule): TResult => {
    if (isPlanMemoDisabled()) {
      return compute(schedule);
    }

    const cached = cache.get(schedule);
    if (cached && cached.plannedOccurrences === schedule.plannedOccurrences) {
      return cached.result;
    }

    const result = compute(schedule);
    cache.set(schedule, {
      plannedOccurrences: schedule.plannedOccurrences,
      result,
    });
    return result;
  };
};

// Same, for derivations that also depend on a few flags; `toKey` names the variant.
export const memoizeByScheduleAndVariant = <TOptions, TResult>(
  toKey: (options: TOptions) => string,
  compute: (schedule: HabitSchedule, options: TOptions) => TResult,
): ((schedule: HabitSchedule, options: TOptions) => TResult) => {
  const cache = new WeakMap<HabitSchedule, Map<string, TResult>>();

  return (schedule: HabitSchedule, options: TOptions): TResult => {
    if (isPlanMemoDisabled()) {
      return compute(schedule, options);
    }

    const variants = cache.get(schedule) ?? new Map<string, TResult>();
    if (!cache.has(schedule)) {
      cache.set(schedule, variants);
    }

    const key = toKey(options);
    if (variants.has(key)) {
      return variants.get(key) as TResult;
    }

    const result = compute(schedule, options);
    variants.set(key, result);
    return result;
  };
};
